import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { accountsAPI } from '../../services/api';
import { listCategories, createCategory, updateCategory, reorderCategories } from '../../services/categories';
const ACCOUNT_COLORS = ['#7C3AED', '#2563EB', '#059669', '#EA580C', '#DB2777', '#0891B2'];
const PALETTE = ['#7C3AED', '#4F46E5', '#2563EB', '#0891B2', '#059669', '#16A34A', '#CA8A04', '#EA580C', '#DC2626', '#DB2777', '#C026D3', '#9333EA'];

const fmt = (amount) => {
  const abs = Math.abs(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${abs} €`;
};

// ── Modal shell ───────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, onSubmit, children, width = 480 }) => {
  const submitRef = useRef(onSubmit);
  useEffect(() => { submitRef.current = onSubmit; });
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Enter' && submitRef.current) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== 'textarea' && tag !== 'select' && tag !== 'button') {
          e.preventDefault();
          submitRef.current();
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel" style={{ maxWidth: width }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

// ── Edit Account Modal ────────────────────────────────────────────────────────
const EditAccountModal = ({ account, onClose, onSave }) => {
  const [label, setLabel] = useState(account.label || '');
  const [bank, setBank] = useState(account.bank || '');
  const [isDefault, setIsDefault] = useState(!!account.isDefault);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (saving) return;
    if (!label.trim()) { setError('Le nom du compte est requis.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await accountsAPI.update(account._id, { label: label.trim(), bank: bank.trim(), isDefault });
      onSave(res?.data?.data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Modifier le compte" onClose={onClose} onSubmit={handleSave} width={440}>
      <div className="modal-fields">
        <div className="field-group">
          <label className="field-label">Banque <span className="field-opt">affiché au-dessus du nom</span></label>
          <input className="field-input" type="text" placeholder="ex : BNP Paribas, Caisse d'Épargne…"
            value={bank} onChange={e => setBank(e.target.value)} maxLength={100} />
        </div>
        <div className="field-group">
          <label className="field-label">Nom du compte</label>
          <input className="field-input" type="text" placeholder="ex : Compte Courant, Livret A…"
            value={label} onChange={e => setLabel(e.target.value)} maxLength={100} />
        </div>
        <div className="field-group">
          <label className="field-label">Compte par défaut</label>
          <button type="button"
            className={`type-btn income${isDefault ? ' active' : ''}`}
            style={{ justifyContent: 'flex-start', gap: 10, padding: '10px 14px' }}
            onClick={() => setIsDefault(d => !d)}>
            <span style={{
              width: 18, height: 18, borderRadius: '50%',
              background: isDefault ? 'var(--green)' : 'var(--bg4)',
              border: `2px solid ${isDefault ? 'var(--green)' : 'var(--border2)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s'
            }}>
              {isDefault && <svg width="10" height="10" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>}
            </span>
            {isDefault ? 'Compte par défaut' : 'Définir comme compte par défaut'}
          </button>
        </div>
      </div>
      {error && <div className="modal-error">{error}</div>}
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </Modal>
  );
};

// ── Category Modal (create + edit) ────────────────────────────────────────────
const CategoryModal = ({ category, accountId, onClose, onSaved }) => {
  const isEdit = !!category;
  const [type, setType] = useState(category?.type || 'expense');
  const [name, setName] = useState(category?.name || '');
  const [color, setColor] = useState(category?.color || '#7C3AED');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (saving) return;
    if (!name.trim()) { setError('Le nom est requis.'); return; }
    setSaving(true);
    setError('');
    try {
      let result;
      if (isEdit) {
        result = await updateCategory(category._id, { name: name.trim(), color });
      } else {
        result = await createCategory({ name: name.trim(), type, accountId, color });
      }
      onSaved(result, isEdit);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Modifier la catégorie' : 'Nouvelle catégorie'} onClose={onClose} onSubmit={handleSave} width={420}>
      <div className="modal-fields">
        {!isEdit && (
          <div className="field-group">
            <label className="field-label">Type</label>
            <div className="type-toggle">
              <button className={`type-btn expense${type === 'expense' ? ' active' : ''}`}
                onClick={() => setType('expense')}>Dépense</button>
              <button className={`type-btn income${type === 'income' ? ' active' : ''}`}
                onClick={() => setType('income')}>Revenu</button>
            </div>
          </div>
        )}
        <div className="field-group">
          <label className="field-label">Nom <span className="field-opt">100 caractères max</span></label>
          <input className="field-input" maxLength={100} value={name}
            onChange={e => setName(e.target.value)} placeholder="ex : Alimentation, Salaire…" />
          <span className="char-count">{name.length}/100</span>
        </div>
        <div className="field-group">
          <label className="field-label">Couleur</label>
          {/* Custom color input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <input
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{
                width: 36, height: 36, borderRadius: 8, border: '1px solid var(--border2)',
                background: 'none', cursor: 'pointer', padding: 2
              }}
              title="Couleur personnalisée"
            />
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>Couleur personnalisée : <strong style={{ color: 'var(--text2)' }}>{color}</strong></span>
          </div>
        </div>
        {/* Preview */}
        <div className="cat-preview">
          <span className="cat-item">
            <span className="cat-name" style={{ color: 'var(--text)' }}>{name || 'Aperçu'}</span>
            <span className="cat-dot" style={{ background: color }} />
          </span>
        </div>
      </div>
      {error && <div className="modal-error">{error}</div>}
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer la catégorie'}
        </button>
      </div>
    </Modal>
  );
};

// ── Drag-and-drop category list ───────────────────────────────────────────────
const CategoryList = ({ cats, onEdit, onReorder }) => {
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = (e, i) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, i) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (i !== dragOverIdx) setDragOverIdx(i);
  };
  const handleDrop = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDragOverIdx(null); return; }
    const next = [...cats];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    onReorder(next);
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  if (cats.length === 0) {
    return <div style={{ padding: '8px 10px', color: 'var(--text3)', fontSize: 12 }}>Aucune catégorie</div>;
  }

  return (
    <div className="cat-list">
      {cats.map((cat, i) => (
        <div
          key={cat._id}
          draggable
          onDragStart={(e) => handleDragStart(e, i)}
          onDragOver={(e) => handleDragOver(e, i)}
          onDrop={(e) => handleDrop(e, i)}
          onDragEnd={handleDragEnd}
          onClick={() => onEdit(cat)}
          className={`cat-item${dragIdx === i ? ' cat-dragging' : ''}${dragOverIdx === i && dragIdx !== i ? ' cat-drag-over' : ''}`}
        >
          <svg className="drag-handle" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <circle cx="3.5" cy="2.5" r="1"/><circle cx="8.5" cy="2.5" r="1"/>
            <circle cx="3.5" cy="6" r="1"/><circle cx="8.5" cy="6" r="1"/>
            <circle cx="3.5" cy="9.5" r="1"/><circle cx="8.5" cy="9.5" r="1"/>
          </svg>
          <span className="cat-dot" style={{ background: cat.color || '#7C3AED' }} />
          <span className="cat-name">{cat.name}</span>
        </div>
      ))}
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();

const Dashboard = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [balances, setBalances] = useState({}); // { [accountId]: { income, expense, balance } }
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [catLoading, setCatLoading] = useState(false);

  // Account editing
  const [editingAccount, setEditingAccount] = useState(null);

  // Category selected account (for the categories section)
  const [catAccountId, setCatAccountId] = useState('');

  // Category modal state
  const [catModal, setCatModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', category }

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // ── Load accounts + balances on mount ──────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setApiError(false);
      try {
        const [accRes, balRes] = await Promise.all([
          accountsAPI.list(),
          accountsAPI.balances(CURRENT_YEAR)
        ]);
        const accs = accRes?.data?.data || [];
        setAccounts(accs);
        const balMap = {};
        (balRes?.data?.data || []).forEach(b => { balMap[b.accountId] = b; });
        setBalances(balMap);
        const defaultAcc = accs.find(a => a.isDefault) || accs[0];
        if (defaultAcc) setCatAccountId(defaultAcc._id);
      } catch (e) {
        console.error(e);
        setApiError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Load categories when catAccountId changes ───────────────────────────────
  const loadCategories = useCallback(async (accountId) => {
    if (!accountId) return;
    setCatLoading(true);
    try {
      const [exp, inc] = await Promise.all([
        listCategories({ type: 'expense', accountId }),
        listCategories({ type: 'income', accountId }),
      ]);
      setCategories([...exp, ...inc]);
    } catch (e) {
      console.error(e);
    } finally {
      setCatLoading(false);
    }
  }, []);

  useEffect(() => {
    if (catAccountId) loadCategories(catAccountId);
  }, [catAccountId, loadCategories]);

  const totalBalance = Object.values(balances).reduce((s, b) => s + (b.balance || 0), 0);
  const totalIncome = Object.values(balances).reduce((s, b) => s + (b.income || 0), 0);
  const totalExpense = Object.values(balances).reduce((s, b) => s + (b.expense || 0), 0);
  const expenseCats = categories.filter(c => c.type === 'expense');
  const incomeCats = categories.filter(c => c.type === 'income');

  // ── Account save handler ────────────────────────────────────────────────────
  const handleAccountSaved = (updated) => {
    if (!updated) return;
    setAccounts(prev => {
      let next = prev.map(a => a._id === updated._id ? updated : a);
      if (updated.isDefault) next = next.map(a => a._id === updated._id ? a : { ...a, isDefault: false });
      return next.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    });
  };

  // ── Category save handler ───────────────────────────────────────────────────
  const handleCategorySaved = (result, isEdit) => {
    if (!result) return;
    if (isEdit) {
      setCategories(prev => prev.map(c => c._id === result._id ? result : c));
    } else {
      setCategories(prev => [...prev, result]);
    }
  };

  // ── Category reorder handler ────────────────────────────────────────────────
  const handleReorder = useCallback(async (type, newCats) => {
    setCategories(prev => [...prev.filter(c => c.type !== type), ...newCats]);
    try {
      await reorderCategories({ accountId: catAccountId, type, orderedIds: newCats.map(c => c._id) });
    } catch (e) {
      console.error(e);
      loadCategories(catAccountId);
    }
  }, [catAccountId, loadCategories]);

  if (loading) {
    return (
      <div className="page-content">
        <div className="loading-overlay"><div className="loading-spinner" /></div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="page-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">Tableau de bord</h1>
            <p className="page-sub">Bonjour, {user?.name?.split(' ')[0] || 'vous'}</p>
          </div>
        </div>
        <div style={{
          background: 'var(--bg2)', border: '1px solid oklch(55% 0.18 25 / 0.3)',
          borderRadius: 'var(--radius-lg)', padding: '32px 28px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
          <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Service indisponible</p>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
            Impossible de contacter le serveur. Vérifiez votre connexion ou que le service est démarré.
          </p>
          <button className="btn-primary" onClick={() => window.location.reload()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord</h1>
          <p className="page-sub">Bonjour, {user?.name?.split(' ')[0] || 'vous'} — voici votre vue d'ensemble</p>
        </div>
        <div className="page-date">{today}</div>
      </div>

      {/* Balance hero */}
      {accounts.length > 0 && (
        <div className="balance-hero">
          <div className="balance-label">Solde total · {CURRENT_YEAR}</div>
          <div className="balance-amount" style={{ color: totalBalance >= 0 ? undefined : '#f87171' }}>
            {totalBalance >= 0 ? '+' : ''}{fmt(totalBalance)}
          </div>
          <div className="balance-sub">
            sur {accounts.length} compte{accounts.length > 1 ? 's' : ''}
            {(totalIncome > 0 || totalExpense > 0) && (
              <>&nbsp;·&nbsp;
                <span style={{ color: '#4ade80' }}>↑ {fmt(totalIncome)}</span>
                &nbsp;
                <span style={{ color: '#f87171' }}>↓ {fmt(totalExpense)}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Accounts — click to edit */}
      <section className="dash-section">
        <div className="section-header">
          <h2 className="section-title">Comptes bancaires</h2>
          <span className="section-count">{accounts.length}</span>
        </div>
        {accounts.length === 0 ? (
          <div className="empty-state"><p>Aucun compte disponible.</p></div>
        ) : (
          <div className="accounts-grid">
            {accounts.map((acc, i) => {
              const b = balances[acc._id] || { income: 0, expense: 0, balance: 0 };
              const balClass = b.balance > 0 ? 'balance-pos' : b.balance < 0 ? 'balance-neg' : 'balance-zero';
              return (
                <button key={acc._id} className="account-card"
                  onClick={() => setEditingAccount(acc)}
                  style={{ textAlign: 'left', width: '100%' }}
                  title="Cliquer pour modifier">
                  <div className="account-top">
                    <div className="account-dot" style={{ background: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length] }} />
                    <div className="account-bank">
                      {acc.bank || <span style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: 11 }}>Banque non renseignée</span>}
                    </div>
                    {acc.isDefault && <span className="badge-default">Par défaut</span>}
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                      style={{ color: 'var(--text3)', marginLeft: 'auto', flexShrink: 0 }}>
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </div>
                  <div className="account-name">{acc.label}</div>
                  <div className="account-year-stats">
                    <div className="acc-stat">
                      <span className="acc-stat-label">Revenus</span>
                      <span className="acc-stat-val income">{fmt(b.income)}</span>
                    </div>
                    <div className="acc-stat">
                      <span className="acc-stat-label">Dépenses</span>
                      <span className="acc-stat-val expense">{fmt(b.expense)}</span>
                    </div>
                    <div className="acc-stat">
                      <span className="acc-stat-label">Solde</span>
                      <span className={`acc-stat-val ${balClass}`}>
                        {b.balance > 0 ? '+' : ''}{fmt(b.balance)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Categories */}
      {accounts.length > 0 && (
        <section className="dash-section">
          <div className="section-header">
            <h2 className="section-title">Catégories</h2>
            <button className="btn-ghost small" onClick={() => setCatModal({ mode: 'create' })}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
              Nouvelle catégorie
            </button>
          </div>

          {/* Account selector tabs for categories */}
          {accounts.length > 1 && (
            <div className="account-tabs" style={{ marginBottom: 16 }}>
              {accounts.map((a, i) => (
                <button key={a._id}
                  className={`account-tab${catAccountId === a._id ? ' active' : ''}`}
                  onClick={() => setCatAccountId(a._id)}>
                  <span className="atab-dot" style={{ background: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length] }} />
                  {a.label}
                  {a.isDefault && <span className="badge-xs">défaut</span>}
                </button>
              ))}
            </div>
          )}

          {catLoading ? (
            <div className="loading-overlay" style={{ padding: 24 }}><div className="loading-spinner" /></div>
          ) : (
            <div className="cat-cols">
              {/* Expense categories */}
              <div className="cat-col">
                <div className="cat-col-header">
                  <span className="cat-type-badge expense">Dépenses</span>
                  <span className="cat-count">{expenseCats.length}</span>
                </div>
                <CategoryList
                  cats={expenseCats}
                  onEdit={(cat) => setCatModal({ mode: 'edit', category: cat })}
                  onReorder={(newCats) => handleReorder('expense', newCats)}
                />
              </div>

              {/* Income categories */}
              <div className="cat-col">
                <div className="cat-col-header">
                  <span className="cat-type-badge income">Revenus</span>
                  <span className="cat-count">{incomeCats.length}</span>
                </div>
                <CategoryList
                  cats={incomeCats}
                  onEdit={(cat) => setCatModal({ mode: 'edit', category: cat })}
                  onReorder={(newCats) => handleReorder('income', newCats)}
                />
              </div>
            </div>
          )}
        </section>
      )}

      {/* Edit Account Modal */}
      {editingAccount && (
        <EditAccountModal
          account={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSave={handleAccountSaved}
        />
      )}

      {/* Category Modal (create or edit) */}
      {catModal && (
        <CategoryModal
          category={catModal.mode === 'edit' ? catModal.category : null}
          accountId={catAccountId}
          onClose={() => setCatModal(null)}
          onSaved={handleCategorySaved}
        />
      )}
    </div>
  );
};

export default Dashboard;
